import * as React from "react";
import { useHistory } from "react-router";
import { toast } from "sonner";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { SocialCoachSessionService } from "@/services";
import { redirectPlayerIfSignUpFinished, saveImageToFirebase, URLS, useSocialCoachSession } from "@/utils";
import { EnvContext } from "@/contexts/appContexts";
import { Button, Card } from "@/components/ui";
import { Png, Svg } from "@/assets";

const IntegratedPlayerPicture: React.FC = () => {
  const history = useHistory();
  const { session, refreshSession } = useSocialCoachSession();
  const envValues = React.useContext(EnvContext);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [imgSrc, setImgSrc] = React.useState("");
  const [imgRef, setImgRef] = React.useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = React.useState<Crop>();
  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const [isCropping, setIsCropping] = React.useState(false);
  const [photoUrl, setPhotoUrl] = React.useState<string>(session?.photoUrl || "");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!photoUrl && session?.photoUrl) {
      setPhotoUrl(session.photoUrl);
    }
    redirectPlayerIfSignUpFinished(session?.signUpStep);
  }, [session]);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => setImgSrc(reader.result?.toString() || ""));
      reader.readAsDataURL(e.target.files[0]);
      setIsCropping(true);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setImgRef(e.currentTarget);

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1,
        width,
        height
      ),
      width,
      height
    );

    setCrop(initialCrop);
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  };

  const handleSaveCrop = async () => {
    if (imgRef && completedCrop && session?.userId) {
      setLoading(true);
      try {
        const blob = await getCroppedImg(imgRef, completedCrop);
        const fileName = `profile_${Date.now()}`;
        const uploadedUrl = await saveImageToFirebase(envValues, session.userId, blob, fileName);
        setPhotoUrl(uploadedUrl);
        setIsCropping(false);
        toast.success("Photo uploaded successfully");
      } catch (e) {
        console.error(e);
        toast.error("Failed to upload photo");
      } finally {
        setLoading(false);
      }
    }
  };

  const nextAction = (): void => {
    history.push(URLS.player.firstSocialNetworks);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await SocialCoachSessionService.updatePlayer({
        firstName: session?.firstName!,
        lastName: session?.lastName!,
        phone: session?.phone!,
        photoUrl: photoUrl,
      });

      refreshSession();
      nextAction();
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-200px)] p-4 sm:p-6">
      <Card className="w-full max-w-[500px] p-6 sm:p-10 shadow-lg border-slate-200">
        <div className="flex flex-col mb-2 sm:mb-4">
          <div className="flex justify-center mb-4 sm:mb-6">
            <img src={Png.Logo} alt="SocialCoach" className="w-48 sm:w-60" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 text-center sm:text-left">
            {isCropping ? "Crop your photo" : "Add a profile picture"}
          </h2>
          <p className="text-slate-500 text-center sm:text-left text-sm sm:text-base mt-1">
            {isCropping
              ? "Adjust the image to fit the circle."
              : "A great photo helps you build trust with your audience."}
          </p>
        </div>

        {isCropping ? (
          <div className="flex flex-col items-center justify-center py-2 sm:py-4 overflow-hidden w-full">
            <div className="max-w-full overflow-hidden flex justify-center bg-slate-50 rounded-lg p-2 border border-slate-100">
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                onComplete={c => setCompletedCrop(c)}
                aspect={1}
                circularCrop
                className="max-w-full"
              >
                <img
                  src={imgSrc}
                  onLoad={onImageLoad}
                  alt="Crop me"
                  className="max-w-full max-h-[40vh] sm:max-h-[50vh] block object-contain"
                />
              </ReactCrop>
            </div>
            <div className="flex flex-col sm:flex-row w-full gap-3 sm:gap-4 mt-6 sm:mt-8">
              <Button
                variant="outline"
                className="w-full sm:flex-1 order-2 sm:order-1"
                onClick={() => setIsCropping(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                className="w-full sm:flex-1 order-1 sm:order-2"
                onClick={handleSaveCrop}
                disabled={loading || !completedCrop}
                loading={loading}
              >
                Use this photo
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 sm:py-6 w-full">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-slate-100 shadow-inner bg-slate-50 mb-8 flex items-center justify-center">
              <img src={photoUrl || Svg.noImageuser} alt="Profile" className="w-full h-full object-cover" />
            </div>

            <div className="flex flex-col w-full gap-2 sm:gap-3">
              {!photoUrl ? (
                <>
                  <Button
                    className="w-full h-11 sm:h-12 text-base"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    Upload Photo
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full h-10 sm:h-11 text-slate-500"
                    onClick={nextAction}
                    disabled={loading}
                  >
                    Skip for now
                  </Button>
                </>
              ) : (
                <>
                  <Button className="w-full h-11 sm:h-12 text-base" onClick={handleSubmit} loading={loading}>
                    Continue
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-10 sm:h-11"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    Change Photo
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full h-10 sm:h-11 text-slate-400 mt-2"
                    onClick={nextAction}
                    disabled={loading}
                  >
                    Skip for now
                  </Button>
                </>
              )}
            </div>

            <input type="file" ref={fileInputRef} onChange={onSelectFile} accept="image/*" className="hidden" />
          </div>
        )}
      </Card>
    </div>
  );
};

export const SetPlayerPicture = IntegratedPlayerPicture;
